import React, { useState, useEffect, useContext, Fragment } from 'react';
import DatePicker from 'react-datepicker';
import { Card, CardBody, Row, Col, CustomInput, FormGroup, Button, Spinner } from 'reactstrap';
import PageHeader from '../../components/common/PageHeader';
import { EnvironmentContext } from '../../context/Context';
import { informesServices } from '../../services';
import { formatDate } from '../../helpers/dateFormatters';
import ExampleCustomInput from '../../components/input/ExampleCustomInput';
import WizardInput from '../../components/auth/wizard/WizardInput';
import DetailRow from '../../components/GestionPersonaEmpresa/personDetails/DetailRow';
import ButtonIcon from '../../components/common/ButtonIcon';
import cogoToast from 'cogo-toast';
import XLSX from 'sheetjs-style';

const InfInteresesPagados = () => {
    const environment = useContext(EnvironmentContext);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listPagadurias, setListPagadurias] = useState([]);
    const [pagaduriaSelected, setPagaduriaSelected] = useState(null);
    const [fechaCorte, setFechaCorte] = useState(new Date());
    const [parsedFechaCorte, setParsedFechaCorte] = useState('');

    useEffect(() => {
        if (!environment || !environment.pagadurias) return;
        const list = environment.pagadurias.map(p => ({
            value: p.id,
            label: p.nombreComercial || p.razonSocial,
        }));
        setListPagadurias(list);
    }, [environment]);

    useEffect(() => {
        if (fechaCorte) setParsedFechaCorte(formatDate(fechaCorte));
    }, [fechaCorte]);

    const onChangePagaduria = (e) => {
        const val = e.target.value;
        if (!val) { setPagaduriaSelected(null); return; }
        const found = environment.pagadurias.find(p => String(p.id) === String(val));
        setPagaduriaSelected(found || null);
    };

    const onSubmit = async () => {
        if (!pagaduriaSelected || !parsedFechaCorte) {
            cogoToast.warn('Por favor completar los campos requeridos', { position: 'bottom-left', heading: 'Campos requeridos' });
            return;
        }
        setLoading(true);
        const response = await informesServices.getInteresesPagados({
            idPagaduria: pagaduriaSelected.id,
            fechaCorte: parsedFechaCorte,
        });
        setLoading(false);
        if (!response || response.error) {
            cogoToast.error(response?.msg || "No se logró completar la operación", { position: 'bottom-left', heading: 'Error al cargar los datos', hideAfter: 6 });
            return;
        }
        setData(response.objResponse);
    };

    const exportar = () => {
        const table = document.getElementById('tablaInteresesPagados');
        const wb = XLSX.utils.table_to_book(table);
        const ws = wb.Sheets[wb.SheetNames[0]];

        // Anchos de columna
        ws['!cols'] = [
            { wch: 15 }, // Fecha Informe
            { wch: 24 }, // N° Documento Asesor
            { wch: 24 }, // N° Documento Cliente
            { wch: 30 }, // Interes del Periodo Pagado (FVE)
            { wch: 30 }, // Interes del Periodo Pagado (FVM)
            { wch: 28 }, // Pago a Intereses Vencidos (FVE)
            { wch: 28 }, // Pago a Intereses Vencidos (FVM)
            { wch: 32 }, // Pagaduría
        ];

        // Negrilla en filas de título (0,1), encabezado de columnas (2) y total (última)
        const range = XLSX.utils.decode_range(ws['!ref']);
        const boldRows = new Set([0, 1, 2, range.e.r]);
        for (let R = range.s.r; R <= range.e.r; R++) {
            if (!boldRows.has(R)) continue;
            for (let C = range.s.c; C <= range.e.c; C++) {
                const addr = XLSX.utils.encode_cell({ r: R, c: C });
                if (!ws[addr]) continue;
                ws[addr].s = { font: { bold: true } };
            }
        }

        XLSX.writeFile(wb, `intereses-pagados-${parsedFechaCorte}.xlsx`);
    };

    const totalFVE = data.reduce((acc, i) => acc + (i.interesDelPeriodoPagadoFVE || 0), 0);
    const totalFVM = data.reduce((acc, i) => acc + (i.interesDelPeriodoPagadoFVM || 0), 0);
    const totalVencidosFVE = data.reduce((acc, i) => acc + (i.pagoInteresesVencidosFVE || 0), 0);
    const totalVencidosFVM = data.reduce((acc, i) => acc + (i.pagoInteresesVencidosFVM || 0), 0);

    return (
        <Fragment>
            <PageHeader title="Informe de Intereses Pagados" className="mb-3" />

            <Card className="my-3">
                <CardBody className="bg-light border-top px-4">
                    <Row>
                        <Col lg={3} className="mx-2">
                            <DetailRow title="Fecha de Corte">
                                <DatePicker
                                    dateFormat="dd/MM/yyyy"
                                    locale="es"
                                    selected={fechaCorte}
                                    onChange={(date) => setFechaCorte(date)}
                                    customInput={<ExampleCustomInput />}
                                />
                            </DetailRow>
                        </Col>
                        <Col lg={3} className="mx-2">
                            <DetailRow title="Pagaduría">
                                <FormGroup>
                                    <WizardInput
                                        type="select"
                                        tag={CustomInput}
                                        value={pagaduriaSelected ? pagaduriaSelected.id : ''}
                                        placeholder="Seleccionar"
                                        id="pagaduria"
                                        name="pagaduria"
                                        options={listPagadurias}
                                        onChange={onChangePagaduria}
                                        errors={[]}
                                    />
                                </FormGroup>
                            </DetailRow>
                        </Col>
                        <Col lg={2} className="mx-2">
                            <DetailRow title="-">
                                <Button onClick={onSubmit} color="primary">
                                    Buscar
                                </Button>
                            </DetailRow>
                        </Col>
                        <Col lg={2} className="mx-2">
                            <DetailRow title="-" titleClass="text-white">
                                <ButtonIcon
                                    onClick={exportar}
                                    icon="file-excel"
                                    transform="shrink-3 down-2"
                                    color="falcon-default"
                                    size="sm"
                                    disabled={!data || !data[0]}
                                >
                                    Exportar
                                </ButtonIcon>
                            </DetailRow>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {loading && (
                <Row className="py-4 d-flex justify-content-center">
                    <Spinner color="primary" />
                </Row>
            )}

            {!loading && data && data[0] && (
                <table
                    id="tablaInteresesPagados"
                    className="table table-dashboard table-striped table-sm fs--1 border-bottom border-200 mb-0 table-dashboard-th-nowrap"
                >
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'center' }} colSpan={8}>INFORME DE INTERESES PAGADOS</th>
                        </tr>
                        <tr>
                            <th style={{ textAlign: 'center' }} colSpan={8}>Con corte al {parsedFechaCorte}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th>Fecha Informe</th>
                            <th>N° Documento Asesor</th>
                            <th>N° Documento Cliente</th>
                            <th style={{ textAlign: 'right' }}>Interes del Periodo Pagado (FVE)</th>
                            <th style={{ textAlign: 'right' }}>Interes del Periodo Pagado (FVM)</th>
                            <th style={{ textAlign: 'right' }}>Pago a Intereses Vencidos (FVE)</th>
                            <th style={{ textAlign: 'right' }}>Pago a Intereses Vencidos (FVM)</th>
                            <th>Pagaduría</th>
                        </tr>
                        {data.map((i, idx) => (
                            <tr key={idx}>
                                <td>{i.fechaInforme}</td>
                                <td>{i.docAsesor}</td>
                                <td>{i.docCliente}</td>
                                <td style={{ textAlign: 'right' }}>{i.interesDelPeriodoPagadoFVE ? i.interesDelPeriodoPagadoFVE.toLocaleString() : 0}</td>
                                <td style={{ textAlign: 'right' }}>{i.interesDelPeriodoPagadoFVM ? i.interesDelPeriodoPagadoFVM.toLocaleString() : 0}</td>
                                <td style={{ textAlign: 'right' }}>{i.pagoInteresesVencidosFVE ? i.pagoInteresesVencidosFVE.toLocaleString() : 0}</td>
                                <td style={{ textAlign: 'right' }}>{i.pagoInteresesVencidosFVM ? i.pagoInteresesVencidosFVM.toLocaleString() : 0}</td>
                                <td>{i.pagaduria}</td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={3} style={{ fontWeight: 'bold' }}>TOTAL</td>
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{totalFVE.toLocaleString()}</td>
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{totalFVM.toLocaleString()}</td>
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{totalVencidosFVE.toLocaleString()}</td>
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{totalVencidosFVM.toLocaleString()}</td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            )}
        </Fragment>
    );
};

export default InfInteresesPagados;
